import { Button, Icon, Input, Text, HStack, VStack } from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { IconographyTypes } from '@genial/icons';
import { Utils, useBreakpointValue } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Pressable } from 'native-base';
import React, { useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { CategoricalChartState } from 'recharts/types/chart/generateCategoricalChart';
import { useRecoilValue } from 'recoil';

import { useOrderActions } from '../../../../../../store/order/actions';
import { orderItemState } from '../../../../../../store/order/atom';
import { hasOrderItem } from '../../../../../../store/order/selectors';
import { useProductActions } from '../../../../../../store/product/actions';
import { structuredOperationProductSelectedState } from '../../../../../../store/product/atom';
import {
  appliedAmountFormData,
  getColorAndIconOperation,
  getStartPointChart,
  messageError,
  validationInvestmentSchema,
} from '../../constants';
import { InvestmentSimulatorSectionProps } from '../StructuredOperationSubDetailsSection';

const axisDefaultProps = {
  fontSize: Typography['fontsize.sm'],
  fontFamily: Typography['fontfamily.base.regular'],
  color: Color['neutral.dark'],
  tickFormatter: (n: number) => Utils.Mask.Percentage(n * 100),
  tickLine: true,
  axisLine: false,
};

export const StructuredOperationInvestmentSimulatorSection: React.FC<
  InvestmentSimulatorSectionProps
> = ({ investmentSimulatorChartData, marketIsOpened, minValue = 0, onClose }) => {
  const data =
    investmentSimulatorChartData?.map((item) => ({
      label: item.label,
      ...item.coordinate,
    })) || [];
  const productItem = useRecoilValue(structuredOperationProductSelectedState);
  const { handleSelectOrderItem, updateOrderStatus } = useOrderActions();
  const { handleSelectProductItem, updateProductStatus } = useProductActions();
  const orderItem = useRecoilValue(orderItemState);
  const hasOrder = useRecoilValue(hasOrderItem);
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const {
    control,
    formState: { errors },
    resetField,
    handleSubmit,
  } = useForm<appliedAmountFormData>({
    mode: 'all',
    reValidateMode: 'onBlur',
    resolver: zodResolver(validationInvestmentSchema(minValue)),
    defaultValues: { appliedAmount: Utils.Mask.Money(minValue) },
  });
  const currentAppliedAmount = useWatch({
    control,
    name: 'appliedAmount',
  });
  const unmaskCurrentAppliedAmout = Utils.Unmask.Money(currentAppliedAmount);
  const [referenceChartItem, setReferenceChartItem] = useState(getStartPointChart(data));
  const iconPropsFocused = {
    name: 'Close',
    size: 'xxs',
    color: Color['brand.primary.principal'],
    variant: 'rounded',
    backgroundColor: Color['neutral.light'],
  } as const;

  const iconPropsUnfocused = {
    name: 'EditPen',
    size: 'xxxs',
    color: Color['brand.tertiary.principal'],
  } as const;

  const iconProps =
    currentAppliedAmount !== Utils.Mask.Money(minValue) ? iconPropsFocused : iconPropsUnfocused;

  const handleUpdateReference = (value: CategoricalChartState) => {
    const activeIndex = value?.activeTooltipIndex;

    if (activeIndex === undefined) {
      return;
    }

    setReferenceChartItem({
      label: data[activeIndex].label!,
      x: data[activeIndex].x!,
      y: data[activeIndex].y!,
    });
  };

  const variationValue =
    referenceChartItem?.x || referenceChartItem?.x === 0
      ? Utils.Mask.Percentage(referenceChartItem?.x * 100)
      : messageError;

  const returnValue =
    referenceChartItem?.y || referenceChartItem?.y === 0
      ? Utils.Mask.Percentage(referenceChartItem?.y * 100)
      : messageError;

  const variationProps = getColorAndIconOperation(referenceChartItem?.x || 0);
  const returnProps = getColorAndIconOperation(referenceChartItem?.y || 0);
  const hasErrorInput = errors?.appliedAmount;

  const isValidValue =
    !hasErrorInput &&
    (currentAppliedAmount || referenceChartItem?.y || referenceChartItem?.y === 0);

  const grossValueAtMaturity = isValidValue
    ? Utils.Mask.Money(unmaskCurrentAppliedAmout * (1 + referenceChartItem!.y!))
    : 'Insira um valor válido';

  const handleSubmitItem = ({ appliedAmount }: appliedAmountFormData) => {
    onClose && onClose();
    const value = Utils.Unmask.Money(appliedAmount);
    handleSelectProductItem({ ...productItem, appliedAmount: value, type: 'StructuredOperations' });
    updateProductStatus({ label: 'Produto selecionado', status: 'completed' });
    updateOrderStatus('opened');

    if (hasOrder) {
      handleSelectOrderItem({ name: orderItem.name, description: orderItem.description });
      updateOrderStatus('completed');
    }
  };

  return (
    <VStack space={Spacing['inline.xxxs']}>
      <Box py={Spacing['inline.xxxs']}>
        <Input
          keyboardType="numeric"
          testId={'modal-structured-operations-details-input-application-amount'}
          name={'appliedAmount'}
          control={control as unknown as Control}
          autoComplete="off"
          hasShadow={false}
          label="Valor do investimento"
          variant="underlined"
          mask={Utils.Mask.InputMoney}
          placeholder={Utils.Mask.Money(minValue)}
          inputRightElement={
            <Pressable onPress={() => resetField('appliedAmount')}>
              <Icon testID="qa" {...iconProps} />
            </Pressable>
          }
          {...(hasErrorInput && { error: hasErrorInput })}
        />
      </Box>
      <VStack space={Spacing['inline.xxxs']}>
        <HStack alignItems={'center'} justifyContent={'space-between'}>
          <Text color={'neutral.black'} variant="regular" fontSize={Typography['fontsize.md']}>
            Variação do ativo
          </Text>
          <HStack alignItems={'center'} space={Spacing['inline.nano']}>
            {variationValue !== messageError && (
              <Icon
                size="xxxxs"
                name={variationProps.icon as IconographyTypes.Option}
                color={Color[variationProps.color]}
                testID="icon-arrow"
              />
            )}
            <Text color={'neutral.black'} variant="regular" fontSize={Typography['fontsize.md']}>
              {variationValue}
            </Text>
          </HStack>
        </HStack>
        <HStack alignItems={'center'} justifyContent={'space-between'}>
          <Text color={'neutral.black'} variant="regular" fontSize={Typography['fontsize.md']}>
            Retorno
          </Text>
          <HStack alignItems={'center'} space={Spacing['inline.nano']}>
            {returnValue !== messageError && (
              <Icon
                size="xxxxs"
                name={returnProps.icon as any}
                color={Color[returnProps.color]}
                testID="icon-arrow"
              />
            )}
            <Text color={'neutral.black'} variant="regular" fontSize={Typography['fontsize.md']}>
              {returnValue}
            </Text>
          </HStack>
        </HStack>
        <HStack alignItems={'center'} justifyContent={'space-between'}>
          <Text color={'neutral.black'} variant="regular" fontSize={Typography['fontsize.md']}>
            Cenário
          </Text>
          <Text color={'neutral.black'} variant="semibold" fontSize={Typography['fontsize.md']}>
            {referenceChartItem?.label || messageError}
          </Text>
        </HStack>
      </VStack>
      <Box w={'full'} bg={'neutral.lightest'} h={'0.5'} />
      <HStack alignItems={'center'} justifyContent={'space-between'}>
        <Text color={'neutral.black'} variant="regular" fontSize={Typography['fontsize.md']}>
          Valor bruto no vencimento
        </Text>
        <Text color={'neutral.black'} variant="semibold" fontSize={Typography['fontsize.lg']}>
          {grossValueAtMaturity}
        </Text>
      </HStack>
      {data && data.length > 0 ? (
        <ResponsiveContainer width="100%" height="100%" minHeight={265}>
          <LineChart
            data={data}
            onClick={handleUpdateReference}
            onMouseMove={handleUpdateReference}>
            <XAxis dataKey="x" {...axisDefaultProps} />
            {!isMobile && <YAxis dataKey="y" {...axisDefaultProps} />}
            <CartesianGrid strokeDasharray={6} />
            <Line
              dot={false}
              type="linear"
              dataKey="y"
              stroke={Color['linechart.bench1']}
              activeDot={{ r: 0 }}
            />
            <ReferenceDot
              ifOverflow="extendDomain"
              x={referenceChartItem.x!}
              y={referenceChartItem.y!}
              r={6}
              fill={Color['neutral.black']}
            />
            <ReferenceLine x={referenceChartItem.x!} stroke={Color['neutral.black']} />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <Box minHeight={265} alignItems={'center'} justifyContent={'center'}>
          <Text variant="regular" textAlign={'center'}>
            Não existem informações suficientes para construir o gráfico do simulador de
            investimentos.
          </Text>
        </Box>
      )}

      <Button
        variant={'callToAction'}
        isDisabled={typeof hasErrorInput?.message === 'string' || !marketIsOpened}
        onPress={handleSubmit(handleSubmitItem)}>
        Confirmar
      </Button>
    </VStack>
  );
};
